import { Component, HostBinding, OnInit, OnDestroy, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../../../services';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[rb-menuitem]',
  imports: [CommonModule, RouterModule, RippleModule],
  template: `
    <ng-container>
      @let menuItem = item();
      <div *ngIf="root() && menuItem.visible !== false" class="layout-menuitem-root-text">
        {{ menuItem.label }}
      </div>
      <a
        *ngIf="(!menuItem.routerLink || menuItem.items) && menuItem.visible !== false"
        [attr.href]="menuItem.url"
        (click)="itemClick($event)"
        [ngClass]="menuItem.styleClass"
        [attr.target]="menuItem.target"
        tabindex="0"
        pRipple>
        <i [ngClass]="menuItem.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ menuItem.label }}</span>
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="menuItem.items"></i>
      </a>
      <a
        *ngIf="menuItem.routerLink && !menuItem.items && menuItem.visible !== false"
        (click)="itemClick($event)"
        [ngClass]="menuItem.styleClass"
        [routerLink]="menuItem.routerLink"
        routerLinkActive="active-route"
        [routerLinkActiveOptions]="
          menuItem.routerLinkActiveOptions || {
            paths: 'subset',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
          }
        "
        [fragment]="menuItem.fragment"
        [queryParamsHandling]="menuItem.queryParamsHandling"
        [preserveFragment]="menuItem.preserveFragment"
        [skipLocationChange]="menuItem.skipLocationChange"
        [replaceUrl]="menuItem.replaceUrl"
        [state]="menuItem.state"
        [queryParams]="menuItem.queryParams"
        [attr.target]="menuItem.target"
        tabindex="0"
        pRipple>
        <i [ngClass]="menuItem.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ menuItem.label }}</span>
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="menuItem.items"></i>
      </a>

      <ul *ngIf="menuItem.items && menuItem.visible !== false" [@children]="submenuAnimation">
        <ng-template ngFor let-child let-i="index" [ngForOf]="menuItem.items">
          <li rb-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child['badgeClass']"></li>
        </ng-template>
      </ul>
    </ng-container>
  `,
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
        })
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
  host: {
    '[class.layout-root-menuitem]': 'root()',
  },
})
export class MenuItemComponent implements OnInit, OnDestroy {
  item = input.required<MenuItem>();
  index = input.required<number>();
  parentKey = input<string>();
  root = input<boolean>();

  active = false;
  menuSourceSubscription: Subscription;
  menuResetSubscription: Subscription;
  key = '';

  constructor(
    public router: Router,
    private layoutService: LayoutService
  ) {
    this.menuSourceSubscription = this.layoutService.menuSource$.subscribe(value => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-');
        } else {
          if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
            this.active = false;
          }
        }
      });
    });

    this.menuResetSubscription = this.layoutService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.item().routerLink) {
        this.updateActiveStateFromRoute();
      }
    });
  }

  ngOnInit() {
    this.key = this.parentKey() ? this.parentKey() + '-' + this.index() : String(this.index());

    if (this.item().routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute() {
    const activeRoute = this.router.isActive(this.item().routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (activeRoute) {
      this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  itemClick(event: Event) {
    const item = this.item();

    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.command) {
      item.command({ originalEvent: event, item: this.item });
    }

    if (item.items) {
      this.active = !this.active;
    }

    this.layoutService.onMenuStateChange({ key: this.key });
  }

  get submenuAnimation() {
    return this.root() ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root();
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
